import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, X } from 'lucide-react';

import type { Lead, ProjectOffering } from '../../../types';
import {
  PROJECT_DOCUMENT_TYPE_LABELS,
  projectDocumentEditorPathFromLabel,
  type ProjectDocumentTypeLabel,
} from '../../../utils/projectRoutes';
import { ChooseOfferingForDocumentModal } from './ChooseOfferingForDocumentModal';
import { isDocumentTypeAvailable } from '../../../lib/documentTypeModules';
import { useModuleAccess } from '../../../hooks/useModuleAccess';
import { useInvoiceSourceChoice } from '../../workReports/components/InvoiceSourceChoice';

type CreateProjectDocumentFlowProps = {
  projectRouteId: string;
  buttonClassName?: string;
};

async function markOfferingLastUsed(offeringId: string): Promise<void> {
  try {
    await fetch(`/api/offerings/${encodeURIComponent(offeringId)}/mark-last-used`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    /* ignore */
  }
}

export function CreateProjectDocumentFlow({
  projectRouteId,
  buttonClassName = 'mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-app-border bg-app-surface text-xs font-semibold text-app-text py-1.5 hover:bg-app-hover transition-colors',
}: CreateProjectDocumentFlowProps) {
  const navigate = useNavigate();
  const [showTypeModal, setShowTypeModal] = React.useState(false);
  const [showOfferingModal, setShowOfferingModal] = React.useState(false);
  const [pendingDocumentTypeLabel, setPendingDocumentTypeLabel] =
    React.useState<ProjectDocumentTypeLabel | null>(null);
  const [projectOfferings, setProjectOfferings] = React.useState<ProjectOffering[]>([]);
  // Jeder Belegtyp nur mit seinem Modul (Angebote, Auftragsabwicklung, Rechnungen).
  const { loaded: modulesLoaded, modules } = useModuleAccess();
  const documentTypeOptions = PROJECT_DOCUMENT_TYPE_LABELS.filter(
    (label) => modulesLoaded && isDocumentTypeAvailable(label, modules),
  );

  const reloadOfferings = React.useCallback(async () => {
    const res = await fetch(`/api/leads/${encodeURIComponent(projectRouteId)}`, {
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Projekt konnte nicht geladen werden.');
    const data = (await res.json()) as Lead;
    setProjectOfferings(data.offerings ?? []);
  }, [projectRouteId]);

  React.useEffect(() => {
    if (!showOfferingModal) return;
    void reloadOfferings().catch(() => {
      setProjectOfferings([]);
    });
  }, [showOfferingModal, reloadOfferings]);

  const openDocumentEditor = React.useCallback(
    (documentLabel: string, offeringId: string, workReportIds?: string[]) => {
      void markOfferingLastUsed(offeringId);
      const fromReports = workReportIds?.length
        ? `&workReports=${encodeURIComponent(workReportIds.join(','))}`
        : '';
      navigate(
        `${projectDocumentEditorPathFromLabel({ id: projectRouteId }, documentLabel, offeringId)}&neu=1${fromReports}`,
      );
      setShowOfferingModal(false);
      setPendingDocumentTypeLabel(null);
      setShowTypeModal(false);
    },
    [navigate, projectRouteId],
  );
  // Rechnung bei offenen Arbeitsberichten: erst fragen, ob aus Angebot oder aus Berichten.
  const { requestDocumentEditor, invoiceSourceModal } = useInvoiceSourceChoice(openDocumentEditor);

  const closeAll = React.useCallback(() => {
    setShowTypeModal(false);
    setShowOfferingModal(false);
    setPendingDocumentTypeLabel(null);
  }, []);

  return (
    <>
      <button type="button" onClick={() => setShowTypeModal(true)} className={buttonClassName}>
        Dokument erstellen
      </button>

      <ChooseOfferingForDocumentModal
        open={showOfferingModal && pendingDocumentTypeLabel != null}
        documentTypeLabel={pendingDocumentTypeLabel ?? 'Angebot'}
        projectRouteId={projectRouteId}
        projectOfferings={projectOfferings}
        onClose={closeAll}
        onBack={() => {
          setShowOfferingModal(false);
          setShowTypeModal(true);
        }}
        onChosen={async (offeringId) => {
          if (!pendingDocumentTypeLabel) return;
          await reloadOfferings();
          setShowOfferingModal(false);
          await requestDocumentEditor(pendingDocumentTypeLabel, offeringId);
        }}
        onOfferingsChange={reloadOfferings}
      />
      {invoiceSourceModal}

      {showTypeModal && (
        <div
          className="fixed inset-0 z-[230] flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px]"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowTypeModal(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-doc-modal-title"
            className="bg-app-surface rounded-md w-full max-w-lg shadow-2xl border border-app-border overflow-hidden flex flex-col max-h-[min(90vh,640px)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-app-border flex items-center gap-3 bg-app-surface-muted/80 shrink-0">
              <button
                type="button"
                onClick={() => setShowTypeModal(false)}
                className="p-1.5 rounded-lg text-app-text-subtle hover:bg-slate-200 hover:text-app-text transition-colors"
                aria-label="Schließen"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h2 id="create-doc-modal-title" className="text-base font-bold text-app-text flex-1 min-w-0">
                Neues Dokument erstellen
              </h2>
              <button
                type="button"
                onClick={() => setShowTypeModal(false)}
                className="p-1.5 rounded-lg text-app-text-subtle hover:bg-slate-200 hover:text-app-text transition-colors shrink-0"
                aria-label="Schließen"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-3 sm:p-4 space-y-2">
              {documentTypeOptions.map((label) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    setShowTypeModal(false);
                    setPendingDocumentTypeLabel(label);
                    setShowOfferingModal(true);
                  }}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-app-border bg-app-surface text-left text-sm font-semibold text-app-text hover:bg-app-hover hover:border-app-border-strong transition-colors"
                >
                  <span>{label}</span>
                  <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
